/**
 * Crew chat: one conversation in three sizes.
 *
 * - Minimized: a launcher at the bottom right with the crew's mark, an unread
 *   count and a mute indicator. Pressing it restores the panel exactly where
 *   you left it - same scroll position, same draft.
 * - Open: a rounded panel beside the crew page, resizable.
 * - Expanded: the same panel, wider. Dragging the three-dot grip (or Fill)
 *   takes it all the way to the navigation rail, and the crew page steps aside.
 *
 * The panel never unmounts while you are on the crew page. Minimizing only
 * hides it, keeping its size and height, so the draft, pending attachments,
 * uploads in flight, the scroll position and any voice connection are exactly
 * as they were. The scroll position is also remembered per crew for when you
 * leave the page and come back. Changing size sends nothing, leaves nothing,
 * disconnects nothing and marks nothing read.
 *
 * Messages are only marked read when the panel is showing, the window is
 * visible and the newest message is on screen.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, fileUrl, socket } from '@/lib/api';
import type { Attachment, Message } from '@/lib/types';
import { bridge } from '@/lib/bridge';
import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';
import { useVoice } from '@/stores/voice';
import { CHAT_MIN, useCrewChat } from '@/stores/crewChat';
import { useMinWidth } from '@/lib/viewport';
import { Icon } from '@/icons/Icon';
import { ChatLog, Composer } from './chat';
import { MenuPanel, type MenuItem } from './Menu';
import { Tooltip } from './Tooltip';
import { Lightbox } from './Lightbox';
import { AudioAttachment, VideoAttachment, VoiceNote, isVoiceNote } from './ChatMedia';
import { useToast } from './ui';
import type { Crew } from './Crew';
import { CrewMark } from './CrewArt';

const URL_RE = /https?:\/\/[^\s<]+/gi;

/** The crew page keeps at least this much room beside the regular panel. */
const PAGE_MIN = 440;
/** Past this little room left for the page, the panel simply takes it all. */
const COVER_AT = 360;

function mergeById(base: Message[], extra: Message[]): Message[] {
  const byId = new Map<string, Message>();
  for (const m of base) byId.set(m.id, m);
  for (const m of extra) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => a.createdAt - b.createdAt);
}

export function CrewChat({ crew }: { crew: Crew }) {
  const channel = 'cc:' + crew.id;
  const queryClient = useQueryClient();
  const toast = useToast();
  const settings = useSession((s) => s.settings);
  const voice = useVoice();

  const mode = useCrewChat((s) => s.mode);
  const setMode = useCrewChat((s) => s.setMode);
  const prefWidth = useCrewChat((s) => s.width);
  const prefExpanded = useCrewChat((s) => s.expandedWidth);
  const setWidth = useCrewChat((s) => s.setWidth);
  const setExpandedWidth = useCrewChat((s) => s.setExpandedWidth);
  const atBottom = useCrewChat((s) => s.atBottom);
  const setAtBottom = useCrewChat((s) => s.setAtBottom);
  const unread = useCrewChat((s) => s.unread[channel] ?? 0);

  /* Beside the page from 1100px; a full-height sheet over it below that. */
  const docked = useMinWidth(1100);

  /* The room there is: the crew page's whole content area, measured. */
  const shellRef = useRef<HTMLElement>(null);
  const [avail, setAvail] = useState(() => window.innerWidth);
  useLayoutEffect(() => {
    const parent = shellRef.current?.parentElement;
    if (!parent) return;
    const measure = () => setAvail(parent.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(parent);
    return () => ro.disconnect();
  }, []);

  const [tab, setTab] = useState<'chat' | 'media'>('chat');
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);
  const [dragWidth, setDragWidth] = useState<number | null>(null);

  /* ---- the conversation ---- */
  const q = useQuery({
    queryKey: ['crew-chat', crew.id],
    queryFn: () => api.get<{ messages: Message[]; more?: boolean; unread: number }>('/api/messages/crew/' + crew.id),
    staleTime: 15_000
  });
  const [messages, setMessages] = useState<Message[]>([]);
  const [more, setMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  useEffect(() => {
    if (!q.data) return;
    setMessages((prev) => mergeById(prev, q.data!.messages));
    setMore(!!q.data.more);
    useCrewChat.getState().setUnread(channel, q.data.unread ?? 0);
  }, [q.data, channel]);

  useEffect(() => {
    const off = [
      socket.on('message:new', (msg: Message) => {
        if (msg.channelId !== channel) return;
        setMessages((l) => (l.some((m) => m.id === msg.id) ? l : [...l, msg]));
      }),
      socket.on('message:edited', (msg: Message) => {
        if (msg.channelId !== channel) return;
        setMessages((l) => l.map((m) => (m.id === msg.id ? msg : m)));
      }),
      socket.on('message:deleted', (d: { messageId: string; channelId?: string }) => {
        if (d.channelId && d.channelId !== channel) return;
        setMessages((l) => l.filter((m) => m.id !== d.messageId));
      }),
      socket.on('message:reaction', (d: { messageId: string; channelId?: string; reactions: Message['reactions'] }) => {
        if (d.channelId && d.channelId !== channel) return;
        setMessages((l) => l.map((m) => (m.id === d.messageId ? { ...m, reactions: d.reactions } : m)));
      }),
      /* Back after a dropped connection: fetch again and merge by id, so
         nothing is shown twice and nothing sent meanwhile is missed. */
      socket.on('ready', () => { void queryClient.invalidateQueries({ queryKey: ['crew-chat', crew.id] }); })
    ];
    return () => off.forEach((fn) => fn());
  }, [channel, crew.id, queryClient]);

  async function loadOlder() {
    const oldest = messages[0];
    if (!oldest || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const res = await api.get<{ messages: Message[]; more?: boolean }>('/api/messages/crew/' + crew.id + '?before=' + encodeURIComponent(oldest.id));
      setMessages((l) => mergeById(l, res.messages));
      setMore(!!res.more);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setLoadingOlder(false);
    }
  }

  /* ---- reading: only what was actually on screen ---- */
  const [pageVisible, setPageVisible] = useState(!document.hidden);
  useEffect(() => {
    const on = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);
  const lastRead = useRef(0);
  const hidden = mode === 'minimized' || mode === 'closed';
  const showing = !hidden && tab === 'chat';
  useEffect(() => {
    if (!showing || !atBottom || !pageVisible) return;
    const newest = messages[messages.length - 1];
    if (!newest) return;
    if (unread === 0 && lastRead.current >= newest.createdAt) return;
    lastRead.current = newest.createdAt;
    void api.post('/api/messages/read', { channelId: channel, at: newest.createdAt }).catch(() => { lastRead.current = 0; });
  }, [showing, atBottom, pageVisible, messages, unread, channel]);

  /* ---- sending, once ---- */
  const nonce = useRef<string | null>(null);
  const send = useCallback(async (body: string, attachments: string[], reply: string | null, spoilers?: string[]) => {
    if (!nonce.current) nonce.current = (crypto.randomUUID?.() ?? Date.now().toString(36) + Math.random().toString(36).slice(2));
    const res = await api.post<{ message: Message }>('/api/messages', {
      scope: 'crew', crewId: crew.id, body, attachments, replyTo: reply, spoilers, nonce: nonce.current
    });
    nonce.current = null;
    setMessages((l) => (l.some((m) => m.id === res.message.id) ? l : [...l, res.message]));
  }, [crew.id]);

  /* ---- notifications for this crew ---- */
  const muted = (settings?.mutedCrews ?? []).includes(crew.id);
  const [permission, setPermission] = useState<NotificationPermission | 'app'>(() =>
    bridge()?.notify ? 'app' : typeof Notification === 'undefined' ? 'denied' : Notification.permission);

  async function setMuted(next: boolean) {
    const list = new Set(settings?.mutedCrews ?? []);
    if (next) list.add(crew.id); else list.delete(crew.id);
    try {
      const res = await api.patch<{ settings: NonNullable<typeof settings> }>('/api/me/settings', { mutedCrews: [...list] });
      useSession.setState({ settings: res.settings });
      toast(next ? 'Windows notifications muted for ' + crew.name : 'Windows notifications on for ' + crew.name, 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  async function askPermission() {
    if (typeof Notification === 'undefined') return;
    try { setPermission(await Notification.requestPermission()); } catch { /* the browser said no */ }
  }

  const notifyItems: MenuItem[] = [
    muted
      ? { label: 'Unmute Windows notifications for this crew', icon: 'bell', onClick: () => void setMuted(false) }
      : { label: 'Mute Windows notifications for this crew', icon: 'bellOff', onClick: () => void setMuted(true) }
  ];
  if (permission === 'default') notifyItems.push({ label: 'Allow desktop notifications…', icon: 'bell', onClick: () => void askPermission() });

  const alertLine =
    settings?.notifications === false ? 'Notifications are off in Settings'
      : muted ? 'Windows alerts muted'
        : permission === 'denied' ? 'Blocked by the browser or Windows'
          : permission === 'default' ? 'Desktop alerts not allowed yet'
            : 'Windows alerts on';

  /* ---- shared media, from the loaded conversation ---- */
  const media = useMemo(() => {
    const pictures: Attachment[] = [];
    const videos: Attachment[] = [];
    const audio: Attachment[] = [];
    const links: { url: string; message: Message }[] = [];
    for (const m of messages) {
      for (const a of m.attachments ?? []) {
        if (a.image) pictures.push(a);
        else if (a.type.startsWith('video/') && !isVoiceNote(a)) videos.push(a);
        else if (a.type.startsWith('audio/') || isVoiceNote(a)) audio.push(a);
      }
      for (const url of (m.body || '').match(URL_RE) ?? []) links.push({ url, message: m });
    }
    return { pictures, videos, audio, links: links.reverse() };
  }, [messages]);
  const [viewer, setViewer] = useState<number | null>(null);

  /* ---- size ---- */
  const expanded = mode === 'expanded';
  const maxOpen = Math.max(CHAT_MIN, avail - PAGE_MIN);
  const maxAll = Math.max(CHAT_MIN, avail);
  /* While dragging you can go anywhere up to the rail; the mode follows on release. */
  const limit = dragWidth !== null || expanded ? maxAll : maxOpen;
  const clamp = (w: number, max = limit) => Math.round(Math.min(max, Math.max(CHAT_MIN, w)));
  /* During a drag the live width lives in a ref and goes straight onto the
     element each frame - React only hears about the start, the end, and the
     moment it crosses into "fills the window". */
  const liveWidth = useRef(0);
  const [dragCover, setDragCover] = useState(false);
  let width = clamp(dragWidth !== null ? liveWidth.current : (expanded ? prefExpanded : prefWidth));
  const covering = docked && !hidden && (dragWidth !== null ? dragCover : expanded && width > avail - COVER_AT);
  if (covering) width = avail;

  useEffect(() => { useCrewChat.getState().setCovering(covering); }, [covering]);
  useEffect(() => () => useCrewChat.getState().setCovering(false), []);

  const dragging = dragWidth !== null;

  function commitWidth(w: number) {
    if (w > maxOpen) {
      setExpandedWidth(w >= avail - COVER_AT ? avail : w);
      setMode('expanded');
    } else if (expanded) {
      setExpandedWidth(w);
    } else {
      setWidth(w);
    }
  }

  /*
   * Dragging the grip.
   *
   * This used to set React state every frame, which re-rendered the whole
   * conversation - every message, the composer, the media players - sixty
   * times a second, and eased each step with a transition on top. That was
   * the lag. Now the width is written straight onto the element once per
   * frame, with no transition, and React renders only at the start and end.
   */
  function startResize(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    const shell = shellRef.current;
    if (!shell) return;
    e.preventDefault();
    const grip = e.currentTarget;
    grip.setPointerCapture(e.pointerId);
    const startX = e.clientX;
    const startW = width;
    let pointerX = startX;
    let covered = covering;
    let frame = 0;
    liveWidth.current = startW;
    const prevCursor = document.body.style.cursor;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const apply = () => {
      frame = 0;
      const w = clamp(startW + (startX - pointerX), maxAll);
      liveWidth.current = w;
      const cover = w > avail - COVER_AT;
      shell.style.width = (cover ? avail : w) + 'px';
      if (cover !== covered) { covered = cover; setDragCover(cover); }
    };
    const move = (ev: PointerEvent) => {
      pointerX = ev.clientX;
      if (!frame) frame = requestAnimationFrame(apply);
    };
    const up = () => {
      grip.removeEventListener('pointermove', move);
      grip.removeEventListener('pointerup', up);
      grip.removeEventListener('pointercancel', up);
      if (frame) { cancelAnimationFrame(frame); apply(); }
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = '';
      commitWidth(liveWidth.current);
      setDragWidth(null);
      setDragCover(false);
    };
    grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', up);
    grip.addEventListener('pointercancel', up);
    setDragCover(covered);
    setDragWidth(startW);
  }

  function onResizeKey(e: React.KeyboardEvent) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    if (e.key === 'Home') { setMode('open'); setWidth(CHAT_MIN); return; }
    if (e.key === 'End') { commitWidth(avail); return; }
    commitWidth(clamp(width + (e.key === 'ArrowLeft' ? 48 : -48), maxAll));
  }

  const inCrewVoice = voice.partyId === crew.voiceRoom;

  const panel = (
    <section
      ref={shellRef}
      aria-label="Crew chat"
      aria-hidden={hidden || undefined}
      // @ts-expect-error - inert is a real attribute; React 19 passes it through
      inert={hidden ? '' : undefined}
      style={{ width: docked ? width : undefined }}
      className={cn(
        'flex h-full min-h-0 flex-col',
        docked
          ? 'relative flex-none py-2 pr-2 pl-3'
          : 'fixed inset-y-0 right-0 z-[150] w-full max-w-[560px] p-2',
        /* Hidden without losing its size, height or scroll: faded out, out of
           the flow and unreachable - never unmounted. */
        hidden ? cn('pointer-events-none invisible translate-x-4 opacity-0', docked && 'absolute inset-y-0 right-0') : 'visible translate-x-0 opacity-100',
        dragging
          ? 'transition-none will-change-[width]'
          : 'transition-[width,opacity,transform,visibility] duration-[280ms] ease-[var(--ease-out)] motion-reduce:transition-none'
      )}
    >
      {docked ? (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize crew chat. Drag, or use the arrow keys; End fills the window."
          aria-valuemin={CHAT_MIN}
          aria-valuemax={Math.round(avail)}
          aria-valuenow={Math.round(width)}
          tabIndex={0}
          title="Drag to resize · double-click to expand or restore"
          onPointerDown={startResize}
          onKeyDown={onResizeKey}
          onDoubleClick={() => setMode(expanded ? 'open' : 'expanded')}
          className="group/grip absolute inset-y-0 left-0 z-[3] flex w-3 cursor-col-resize touch-none items-center justify-center outline-none"
        >
          {/* Three dots in the middle - the one place to take hold of it. */}
          <span
            className={cn(
              'flex w-[8px] flex-col items-center justify-center gap-[5px] rounded-full transition-[background-color,height,box-shadow] duration-[250ms] ease-[var(--ease-out)]',
              dragging
                ? 'h-24 bg-accent-soft shadow-[0_0_0_1px_var(--accent-line)]'
                : 'h-14 bg-surface-2 shadow-[0_0_0_1px_var(--line-soft)] group-hover/grip:h-20 group-hover/grip:bg-surface-3 group-focus-visible/grip:h-20 group-focus-visible/grip:shadow-[0_0_0_2px_var(--accent)]'
            )}
          >
            {[0, 1, 2].map((i) => (
              <i
                key={i}
                aria-hidden
                className={cn(
                  'block h-[4px] w-[4px] rounded-full transition-colors duration-200',
                  dragging ? 'bg-accent' : 'bg-mute group-hover/grip:bg-ink group-focus-visible/grip:bg-accent'
                )}
              />
            ))}
          </span>
        </div>
      ) : null}

      {/* The card itself: rounded all round, with a soft edge drawn on top. */}
      <div className="smooth-clip relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[calc(22px*var(--radius-mul,1))] bg-surface-1 shadow-[var(--shadow-2)]">
        {/* ---- header: one compact line, window controls in the top right ---- */}
        <header className="flex flex-none items-center gap-2.5 py-2 pr-2 pl-3">
          <CrewMark tag={crew.tag} appearance={crew.appearance} size={34} className="rounded-[calc(12px*var(--radius-mul,1))]" />
          <div className="min-w-0 flex-1">
            <h2 className="m-0 truncate text-[15px] leading-tight font-bold text-ink">Crew chat</h2>
            <p className="m-0 truncate text-[12px] leading-tight text-mute">
              <span className="font-semibold tracking-[.02em] uppercase">{crew.name}</span>
              <span aria-hidden> · </span>
              {crew.memberCount} {crew.memberCount === 1 ? 'member' : 'members'}
            </p>
          </div>
          <div className="flex flex-none items-center gap-1">
            {crew.myRole && !inCrewVoice ? (
              <RevealButton
                small
                tone="accent"
                icon="headset"
                label="Join voice"
                onClick={() => void voice.join(crew.voiceRoom).catch((err: Error) => toast(err.message, 'bad'))}
              />
            ) : inCrewVoice ? (
              <RevealButton small tone="good" icon="headset" label="In voice" onClick={() => undefined} />
            ) : null}
            <RevealButton
              small
              icon={muted || permission === 'denied' ? 'bellOff' : 'bell'}
              label={alertLine}
              onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMenuAt({ x: Math.max(8, r.right - 300), y: r.bottom + 6 }); }}
            />
          </div>
          <span aria-hidden className="mx-0.5 h-5 w-px flex-none bg-line-soft" />
          <div role="group" aria-label="Chat window" className="flex flex-none items-center gap-0.5">
            <WinButton icon="minimize" label="Minimize" onClick={() => setMode('minimized')} />
            {docked ? (
              <WinButton
                icon={covering ? 'restore' : 'maximize'}
                label={covering ? 'Restore' : 'Maximize'}
                onClick={() => { if (covering) setMode('open'); else { setExpandedWidth(avail); setMode('expanded'); } }}
              />
            ) : null}
            <WinButton icon="close" label="Close chat" danger onClick={() => setMode('closed')} />
          </div>
        </header>

        {/* ---- tabs ---- */}
        <div role="tablist" aria-label="Crew chat views" className="mx-4 flex flex-none gap-6 border-b border-line-soft">
          {([['chat', 'Chat'], ['media', 'Shared media']] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={cn(
                'relative h-10 text-[14px] font-semibold outline-none transition-colors duration-[250ms] focus-visible:shadow-[inset_0_0_0_2px_var(--accent)]',
                tab === id ? 'text-ink' : 'text-mute hover:text-dim'
              )}
            >
              {label}
              <span
                aria-hidden
                className={cn(
                  'absolute inset-x-0 -bottom-px h-[3px] origin-center rounded-full bg-accent transition-[opacity,transform] duration-[280ms] ease-[var(--ease-out)]',
                  tab === id ? 'scale-x-100 opacity-100' : 'scale-x-50 opacity-0'
                )}
              />
            </button>
          ))}
        </div>

        {/* ---- body: both views stay mounted, so switching keeps the draft and the scroll ---- */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          {q.isError && !messages.length ? (
            <div role="alert" className="m-4 rounded-[var(--radius-l)] bg-surface-2 p-4 text-[14px] text-dim">
              Crew chat could not load.{' '}
              <button type="button" className="font-semibold text-accent hover:underline" onClick={() => void q.refetch()}>Try again</button>
            </div>
          ) : null}

          <div className={cn('flex min-h-0 flex-1 flex-col transition-opacity duration-[250ms]', tab !== 'chat' && 'invisible opacity-0')}>
            <ChatLog
              messages={messages}
              canModerate={crew.myRole === 'owner' || crew.myRole === 'officer'}
              onReply={setReplyTo}
              hasOlder={more}
              loadingOlder={loadingOlder}
              onLoadOlder={() => void loadOlder()}
              onBottomChange={setAtBottom}
              memoryKey={channel}
              paused={hidden || tab !== 'chat'}
              emptyHint={q.isPending ? 'Loading crew chat…' : 'Say hello to your crew.'}
            />
            {crew.myRole ? (
              <Composer
                focusKey={channel}
                poll={{ scope: 'crew', channelId: channel }}
                people={crew.members.map((m) => m.user)}
                placeholder="Message your crew…"
                replyTo={replyTo}
                onCancelReply={() => setReplyTo(null)}
                onSend={send}
              />
            ) : (
              <p className="m-0 border-t border-line-soft px-4 py-3 text-[14px] text-mute">Join the crew to chat.</p>
            )}
          </div>

          {tab === 'media' ? (
            <div className="absolute inset-0 overflow-y-auto bg-surface-1 p-4 animate-[fade-in_250ms_ease-out] motion-reduce:animate-none">
              <SharedMedia media={media} onOpenPicture={setViewer} />
            </div>
          ) : null}
        </div>

        <span aria-hidden className="pointer-events-none absolute inset-0 z-[4] rounded-[inherit] shadow-[inset_0_0_0_1px_var(--line-soft)]" />
      </div>

      {menuAt ? <MenuPanel at={menuAt} items={notifyItems} onClose={() => setMenuAt(null)} /> : null}
      {viewer !== null ? <Lightbox list={media.pictures} index={viewer} onClose={() => setViewer(null)} /> : null}
    </section>
  );

  return (
    <>
      {panel}
      {!docked && !hidden ? (
        <button
          type="button"
          aria-label="Close crew chat"
          onClick={() => setMode('minimized')}
          className="fixed inset-0 z-[140] bg-black/35 backdrop-blur-[1px] animate-[fade-in_220ms_ease-out]"
        />
      ) : null}
      {mode === 'minimized' ? createPortal(
        <button
          type="button"
          onClick={() => setMode(lastOpenMode.current)}
          aria-label={'Open crew chat' + (unread ? ', ' + unread + ' unread' : '') + (muted ? ', Windows notifications muted' : '')}
          className="crew-launcher fixed right-5 bottom-5 z-[160] flex h-14 items-center gap-3 rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 pr-4 pl-2.5 shadow-[inset_0_0_0_1px_var(--line),var(--shadow-3)] outline-none transition-[transform,box-shadow] duration-[250ms] ease-[var(--ease-out)] hover:-translate-y-0.5 focus-visible:shadow-[0_0_0_3px_var(--accent)] motion-reduce:hover:translate-y-0"
        >
          <CrewMark tag={crew.tag} appearance={crew.appearance} size={38} className="rounded-[calc(12px*var(--radius-mul,1))]" />
          <span className="text-[15px] font-bold text-ink">Crew chat</span>
          {unread ? (
            <span className="grid h-6 min-w-6 place-items-center rounded-full bg-bad px-1.5 text-[12px] font-bold text-white tabular-nums" aria-hidden>
              {unread > 99 ? '99+' : unread}
            </span>
          ) : null}
          <Icon name={muted ? 'bellOff' : 'bell'} size={17} className="text-mute" />
          <Icon name="chevronR" size={16} className="text-ghost" />
          <style>{`@keyframes launcher-in { from { opacity: 0; transform: translateY(10px) scale(.98) } to { opacity: 1; transform: none } } .crew-launcher { animation: launcher-in 280ms cubic-bezier(.16,1,.3,1) both } @media (prefers-reduced-motion: reduce) { .crew-launcher { animation: none } }`}</style>
        </button>,
        document.body
      ) : null}
      <RememberMode mode={mode} into={lastOpenMode} />
    </>
  );
}

/* The size chat was last shown at, so the launcher brings it back at that size. */
const lastOpenMode: { current: 'open' | 'expanded' } = { current: 'open' };
function RememberMode({ mode, into }: { mode: string; into: { current: 'open' | 'expanded' } }) {
  useEffect(() => { if (mode === 'open' || mode === 'expanded') into.current = mode; }, [mode, into]);
  return null;
}

/**
 * An icon button whose label slides out while you point at it or focus it.
 *
 * Icons only at rest keep the header calm; the words are one hover away and
 * always there for screen readers.
 */
function RevealButton({ icon, label, onClick, tone = 'plain', small }: {
  icon: string;
  label: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  tone?: 'plain' | 'accent' | 'good';
  small?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        'group/rb flex items-center rounded-[calc(10px*var(--radius-mul,1))] font-semibold outline-none',
        small ? 'h-8 px-[8px] text-[12.5px]' : 'h-10 px-[11px] text-[13.5px]',
        'transition-[background-color,color,box-shadow] duration-[250ms] ease-[var(--ease-out)] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
        tone === 'accent'
          ? 'bg-accent-soft text-accent shadow-[inset_0_0_0_1px_var(--accent-line)] hover:bg-[color-mix(in_srgb,var(--accent)_24%,transparent)]'
          : tone === 'good'
            ? 'bg-[color-mix(in_srgb,var(--good)_12%,transparent)] text-good shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--good)_35%,transparent)]'
            : 'bg-surface-2 text-dim shadow-[inset_0_0_0_1px_var(--line-soft)] hover:bg-surface-3 hover:text-ink'
      )}
    >
      <Icon name={icon} size={small ? 15 : 17} className="flex-none" />
      <span
        aria-hidden
        className="grid grid-cols-[0fr] opacity-0 transition-[grid-template-columns,opacity,margin] duration-[280ms] ease-[var(--ease-out)] group-hover/rb:ml-2 group-hover/rb:grid-cols-[1fr] group-hover/rb:opacity-100 group-focus-visible/rb:ml-2 group-focus-visible/rb:grid-cols-[1fr] group-focus-visible/rb:opacity-100 motion-reduce:transition-none"
      >
        <span className="overflow-hidden whitespace-nowrap">{label}</span>
      </span>
    </button>
  );
}

/** Minimize, maximize and close, in the chat window's top-right corner. */
function WinButton({ icon, label, onClick, danger }: {
  icon: string;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <Tooltip content={label} side="bottom">
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        className={cn(
          'grid h-8 w-8 place-items-center rounded-[calc(10px*var(--radius-mul,1))] text-mute outline-none',
          'transition-[background-color,color,transform] duration-200 ease-[var(--ease-out)] active:scale-95 focus-visible:shadow-[0_0_0_2px_var(--accent)]',
          danger ? 'hover:bg-bad hover:text-white' : 'hover:bg-surface-3 hover:text-ink'
        )}
      >
        <Icon name={icon} size={15} />
      </button>
    </Tooltip>
  );
}

function SharedMedia({ media, onOpenPicture }: {
  media: { pictures: Attachment[]; videos: Attachment[]; audio: Attachment[]; links: { url: string; message: Message }[] };
  onOpenPicture: (index: number) => void;
}) {
  const empty = !media.pictures.length && !media.videos.length && !media.audio.length && !media.links.length;
  if (empty) return <p className="m-0 py-10 text-center text-[14px] text-mute">Pictures, clips, voice notes and links shared in crew chat collect here.</p>;

  return (
    <div className="flex flex-col gap-6">
      {media.pictures.length ? (
        <section aria-label="Pictures" className="pugo-panel rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-3">
          <h3 className="m-0 mb-2 text-[14px] font-semibold text-mute">Pictures ({media.pictures.length})</h3>
          <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(120px,1fr))] gap-2 p-0">
            {media.pictures.map((a, i) => (
              <li key={a.id}>
                <button type="button" onClick={() => onOpenPicture(i)} aria-label={'Open ' + a.name} className="smooth-clip block aspect-square w-full overflow-hidden rounded-[calc(14px*var(--radius-mul,1))] bg-surface-3 outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]">
                  <img src={fileUrl(a.url)} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 hover:scale-[1.04] motion-reduce:hover:scale-100" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {media.videos.length ? (
        <section aria-label="Videos" className="pugo-panel rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-3">
          <h3 className="m-0 mb-2 text-[14px] font-semibold text-mute">Videos ({media.videos.length})</h3>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {media.videos.map((a) => (
              <li key={a.id}>
                <VideoAttachment a={a} src={fileUrl(a.url)} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {media.audio.length ? (
        <section aria-label="Audio" className="pugo-panel rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-3">
          <h3 className="m-0 mb-2 text-[14px] font-semibold text-mute">Audio and voice notes ({media.audio.length})</h3>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {media.audio.map((a) => (
              <li key={a.id}>
                {isVoiceNote(a) ? <VoiceNote a={a} src={fileUrl(a.url)} /> : <AudioAttachment a={a} src={fileUrl(a.url)} />}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {media.links.length ? (
        <section aria-label="Links" className="pugo-panel rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-3">
          <h3 className="m-0 mb-2 text-[14px] font-semibold text-mute">Links ({media.links.length})</h3>
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {media.links.map(({ url, message }, i) => {
              let host = url;
              try { host = new URL(url).hostname; } catch { /* keep the raw text */ }
              return (
                <li key={message.id + i}>
                  <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 px-3 py-2.5 text-[14px] text-ink no-underline outline-none transition-colors duration-[250ms] hover:bg-surface-3 focus-visible:shadow-[0_0_0_2px_var(--accent)]">
                    <Icon name="link" size={16} className="flex-none text-mute" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">{host}</span>
                      <span className="block truncate text-[12.5px] text-mute">{url}</span>
                    </span>
                  </a>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
